/**
 * STKR cart drawer recommendations carousel + free-gift progress keeper.
 *
 * Ranking is Levi's Dawn-era scoring, ported unchanged:
 *   score = wQ * quantity share + wN * how many cart products recommended it
 *         + wP * average (inverted) rank position
 * Additions in the Horizon port: skip products already in the cart, skip sold
 * out and $0 products (settings), and cache each product's recommendations so
 * quantity changes don't refetch.
 *
 * Horizon redraws the drawer by diffing fresh server HTML into the page, which
 * removes anything injected by script. Both elements below re-attach removed
 * children when a redraw empties them.
 *
 * Dr Free Gift knows how to refresh Dawn's drawer but not Horizon's, so after
 * adding or removing a gift it reloads the whole page. The app exposes its
 * refresher as window.__docappCartReload; installFreeGiftDrawerRefresh() swaps
 * its drawer step for a Horizon cart refresh (theme setting, on by default).
 *
 * Empty-cart mode (added 2026-09-28, theme-surgery/2026-09-28-empty-cart-recommendations):
 * when the cart is empty, snippets/stkr-cart-upsell.liquid renders the carousel with
 * data-mode="collection" and its cards already filled in from a collection (Most
 * Popular) on the server. There is nothing to rank, so #refresh() must not run in
 * that mode: with an empty cart it would call #render([]) and wipe the server cards.
 * Everything else (Add button, variant dropdown, drag, arrows) is shared and works
 * unchanged. Adding a product from the empty drawer redraws the drawer into its full
 * state, which renders a fresh carousel in the normal recommendations mode.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** @type {Map<string, Promise<Candidate[]>>} */
const recommendationCache = new Map();

/**
 * @typedef {{ id: number, price: number, available: boolean, tags: string[], node: HTMLElement }} Candidate
 */

/**
 * Watches `target` and puts back its element children when a drawer redraw
 * removes all of them without adding replacements.
 * @param {HTMLElement} target
 */
function keepChildren(target) {
  const observer = new MutationObserver((records) => {
    if (target.children.length > 0) return;
    const removed = [];
    let added = false;
    for (const record of records) {
      for (const node of record.addedNodes) if (node.nodeType === Node.ELEMENT_NODE) added = true;
      for (const node of record.removedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && !node.isConnected) removed.push(node);
      }
    }
    if (added || removed.length === 0) return;
    target.append(...removed);
    observer.takeRecords();
  });
  observer.observe(target, { childList: true });
  return observer;
}

class StkrKeepChildren extends HTMLElement {
  /** @type {MutationObserver[]} */
  #observers = [];

  connectedCallback() {
    for (const child of this.children) {
      if (child instanceof HTMLElement) this.#observers.push(keepChildren(child));
    }
  }

  disconnectedCallback() {
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
  }
}

class StkrCartUpsell extends HTMLElement {
  // data-mode is watched too (added 2026-09-28): if a drawer redraw ever reuses this element
  // and flips it from the empty-cart collection mode to the normal mode, re-rank right away.
  static observedAttributes = ['data-cart', 'data-mode'];

  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #renderedKey = '';
  #token = 0;
  /** @type {{ startX: number, startScroll: number, moved: boolean } | null} */
  #drag = null;
  #suppressClick = false;

  connectedCallback() {
    this.#list = this.querySelector('[data-stkr-upsell-list]');
    if (!this.#list) return;
    this.#observer = keepChildren(this.#list);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('click', this.#onClickCapture, true);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('dragstart', this.#onDragStart);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('click', this.#onClickCapture, true);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('dragstart', this.#onDragStart);
    this.#endDrag();
  }

  /*
   * Click-and-drag scrolling for mouse users (touch already swipes natively).
   * A drag past a few pixels is not a click, so it never adds to cart or opens
   * a product by accident.
   */

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.dataset.drag === 'false') return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (!this.#list || !this.#list.contains(target) || target.closest('select')) return;
    this.#drag = { startX: event.clientX, startScroll: this.#list.scrollLeft, moved: false };
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || !this.#list) return;
    const distance = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(distance) < 6) return;
      drag.moved = true;
      this.#list.classList.add('is-dragging');
    }
    event.preventDefault();
    this.#list.scrollLeft = drag.startScroll - distance;
  };

  #onPointerUp = () => {
    const moved = this.#drag?.moved;
    this.#endDrag();
    if (!moved) return;
    // The click that follows this pointerup belongs to the drag; swallow it.
    this.#suppressClick = true;
    window.setTimeout(() => {
      this.#suppressClick = false;
    }, 0);
  };

  #endDrag() {
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    this.#list?.classList.remove('is-dragging');
    this.#drag = null;
  }

  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** Stop the browser's native image/link drag ghost from hijacking the drag. */
  /** @param {DragEvent} event */
  #onDragStart = (event) => {
    if (this.dataset.drag !== 'false' && this.#list?.contains(/** @type {Node} */ (event.target))) {
      event.preventDefault();
    }
  };

  /**
   * The drawer redraw rewrites data-cart from the server after every cart change.
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected || !this.#list) return;
    // Leaving the empty-cart collection mode: forget what was rendered so #refresh()
    // cannot skip itself on a stale key, then rank for the new cart. (2026-09-28)
    if (name === 'data-mode') this.#renderedKey = '';
    this.#refresh();
  }

  /**
   * True when this carousel shows a collection's products in the empty-cart drawer
   * instead of ranked recommendations (data-mode="collection", set by the Liquid snippet).
   * Added 2026-09-28.
   */
  get #isCollectionMode() {
    return this.dataset.mode === 'collection';
  }

  get #settings() {
    const d = this.dataset;
    const number = (value, fallback) => {
      const parsed = parseFloat(value ?? '');
      return Number.isNaN(parsed) ? fallback : parsed;
    };
    return {
      url: d.url || '/recommendations/products',
      section: d.section || 'stkr-cart-upsell-recs',
      intent: d.intent === 'complementary' ? 'complementary' : 'related',
      pool: Math.max(1, Math.min(10, number(d.pool, 8))),
      show: Math.max(1, number(d.show, 6)),
      weightQuantity: number(d.weightQuantity, 0.3),
      weightFrequency: number(d.weightFrequency, 0.4),
      weightPosition: number(d.weightPosition, 0.3),
      exclusions: new Set(
        (d.exclusions || '')
          .split(/[\s,]+/)
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id))
      ),
      excludeTags: new Set(
        (d.excludeTags || '')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      ),
      hideInCart: d.hideInCart !== 'false',
      hideSoldOut: d.hideSoldOut !== 'false',
      hideZeroPrice: d.hideZeroPrice !== 'false',
      addedLabel: d.addedLabel || 'Added',
    };
  }

  /**
   * Cart lines grouped by product (Levi's combineVariants). $0 lines such as free
   * gifts count as "in the cart" but are not used as recommendation sources.
   */
  #cartProducts() {
    /** @type {Map<number, number>} */
    const combined = new Map();
    /** @type {Set<number>} */
    const inCart = new Set();
    for (const entry of (this.dataset.cart || '').split(',')) {
      const [id, qty, paid] = entry.split(':').map((value) => parseInt(value, 10));
      if (Number.isNaN(id)) continue;
      inCart.add(id);
      if (paid === 0) continue;
      combined.set(id, (combined.get(id) || 0) + (Number.isNaN(qty) ? 1 : qty));
    }
    return {
      sources: Array.from(combined, ([productId, quantity]) => ({ productId, quantity })),
      inCart,
    };
  }

  async #refresh() {
    // Empty-cart collection mode: the server already rendered the cards and there is no cart
    // to rank against. Doing nothing here keeps those cards on screen. (2026-09-28)
    if (this.#isCollectionMode) return;

    const settings = this.#settings;
    const { sources: cartProducts, inCart } = this.#cartProducts();
    const key = `${this.dataset.cart}|${settings.pool}|${settings.intent}|${settings.show}`;
    if (key === this.#renderedKey && this.#list?.children.length) return;

    const token = ++this.#token;
    if (cartProducts.length === 0) return this.#render([], key);

    const lists = await Promise.all(cartProducts.map(({ productId }) => this.#fetchCandidates(productId, settings)));
    if (token !== this.#token) return;

    const ranked = rankRecommendations(lists, cartProducts, inCart, settings);
    this.#render(ranked.slice(0, settings.show), key);
  }

  /**
   * @param {number} productId
   * @param {ReturnType<StkrCartUpsell['#settings']>} settings
   * @returns {Promise<Candidate[]>}
   */
  #fetchCandidates(productId, settings) {
    const cacheKey = `${productId}|${settings.pool}|${settings.intent}`;
    const cached = recommendationCache.get(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams({
      product_id: String(productId),
      limit: String(settings.pool),
      section_id: settings.section,
    });
    if (settings.intent === 'complementary') params.set('intent', 'complementary');

    const request = fetch(`${settings.url}?${params}`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('.stkr-upsell__card')).map((node) => ({
          id: parseInt(node.getAttribute('data-product-id') || '', 10),
          price: parseInt(node.getAttribute('data-price') || '0', 10),
          available: node.getAttribute('data-available') === 'true',
          tags: (node.getAttribute('data-tags') || '').split(',').map((tag) => tag.trim()),
          node: /** @type {HTMLElement} */ (node),
        }));
      })
      .catch((error) => {
        console.warn('[stkr-cart-upsell] recommendations failed', productId, error);
        recommendationCache.delete(cacheKey);
        return [];
      });

    recommendationCache.set(cacheKey, request);
    return request;
  }

  /**
   * @param {Candidate[]} winners
   * @param {string} key
   */
  #render(winners, key) {
    if (!this.#list) return;
    const nodes = winners.map((winner) => document.importNode(winner.node, true));
    this.#list.replaceChildren(...nodes);
    this.#list.scrollLeft = 0;
    this.#observer?.takeRecords();
    this.#renderedKey = key;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const scroll = target.closest('[data-stkr-upsell-scroll]');
    if (scroll && this.#list) {
      const direction = Number(scroll.getAttribute('data-stkr-upsell-scroll')) || 1;
      this.#list.scrollBy({ left: direction * this.#list.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }
    const button = target.closest('[data-stkr-upsell-add]');
    if (button instanceof HTMLButtonElement && !button.disabled) this.#add(button);
  };

  /** Variant dropdown: move price and the add button to the chosen variant. */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-stkr-upsell-variant]')) return;
    const card = select.closest('.stkr-upsell__card');
    const option = select.selectedOptions[0];
    const button = card?.querySelector('[data-stkr-upsell-add]');
    const price = card?.querySelector('[data-stkr-upsell-price]');
    if (!card || !option || !(button instanceof HTMLButtonElement)) return;
    button.dataset.variantId = option.value;
    button.disabled = option.disabled;
    if (price && option.dataset.price) price.textContent = option.dataset.price;
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = parseInt(button.dataset.variantId || '', 10);
    if (Number.isNaN(variantId)) return;

    const card = button.closest('.stkr-upsell__card');
    card?.querySelector('.stkr-upsell__error')?.remove();
    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const routes = window.Theme?.routes || {};
    try {
      const addUrl = `${(routes.cart_add_url || '/cart/add').replace(/\.js$/, '')}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const cart = await fetch(`${(routes.cart_url || '/cart').replace(/\.js$/, '')}.js`, {
        cache: 'no-store',
      }).then((result) => result.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'stkr-cart-upsell',
          sourceId: this.id || 'stkr-cart-upsell',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
      button.textContent = this.#settings.addedLabel;

      // Safety net: other scripts (gift app, backorder reconciler) can change the
      // cart behind the drawer's back, and then the redraw above may not land.
      // If the drawer still shows the old cart shortly after, force a refresh,
      // and never leave the button stuck disabled.
      const cartBefore = this.dataset.cart;
      window.setTimeout(() => {
        if (this.isConnected && this.dataset.cart === cartBefore) {
          refreshCartUI().catch((error) => console.warn('[stkr-cart-upsell] cart refresh failed', error));
        }
      }, 1500);
      window.setTimeout(() => {
        if (button.isConnected) {
          button.disabled = false;
          button.textContent = label;
        }
      }, 3000);
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
      button.textContent = label;
      const message = document.createElement('p');
      message.className = 'stkr-upsell__error';
      message.setAttribute('role', 'alert');
      message.textContent = error instanceof Error ? error.message : 'Could not add this item.';
      button.after(message);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

/**
 * Levi's scoring from the Dawn theme (cart-drawer-recommendations.js), unchanged
 * apart from the added skip rules.
 * @param {Candidate[][]} lists - one ranked list per cart product
 * @param {{ productId: number, quantity: number }[]} cartProducts - paid cart products (the sources)
 * @param {Set<number>} inCart - every product in the cart, including $0 gifts
 * @param {ReturnType<StkrCartUpsell['#settings']>} settings
 */
function rankRecommendations(lists, cartProducts, inCart, settings) {
  const totalCartQuantity = cartProducts.reduce((sum, item) => sum + item.quantity, 0) || 1;
  const totalCartItems = cartProducts.length || 1;
  const maxPosition = settings.pool;

  /** @type {Map<number, Candidate & { recommendedBy: { quantity: number, invertedPosition: number }[] }>} */
  const merged = new Map();

  lists.forEach((candidates, index) => {
    const sourceQuantity = cartProducts[index]?.quantity || 1;
    candidates.forEach((candidate, position) => {
      if (!candidate || Number.isNaN(candidate.id)) return;
      if (settings.exclusions.has(candidate.id)) return;
      if (candidate.tags.some((tag) => settings.excludeTags.has(tag))) return;
      if (settings.hideInCart && inCart.has(candidate.id)) return;
      if (settings.hideSoldOut && !candidate.available) return;
      if (settings.hideZeroPrice && candidate.price <= 0) return;

      const invertedPosition = maxPosition + 1 - (position + 1);
      const existing = merged.get(candidate.id);
      if (existing) {
        existing.recommendedBy.push({ quantity: sourceQuantity, invertedPosition });
      } else {
        merged.set(candidate.id, { ...candidate, recommendedBy: [{ quantity: sourceQuantity, invertedPosition }] });
      }
    });
  });

  const scored = Array.from(merged.values()).map((rec) => {
    const totalQuantity = rec.recommendedBy.reduce((sum, item) => sum + item.quantity, 0);
    const timesRecommended = rec.recommendedBy.length;
    const avgInvertedPosition =
      rec.recommendedBy.reduce((sum, item) => sum + item.invertedPosition, 0) / rec.recommendedBy.length;

    const quantityShare = totalQuantity / totalCartQuantity;
    const frequencyShare = timesRecommended / totalCartItems;
    const positionShare = avgInvertedPosition / maxPosition;

    const score =
      settings.weightQuantity * quantityShare +
      settings.weightFrequency * frequencyShare +
      settings.weightPosition * positionShare;

    return { ...rec, score };
  });

  // Array.prototype.sort is stable, so ties keep first-seen order like the original.
  scored.sort((a, b) => b.score - a.score);
  return scored;
}

/** Ask every cart-items-component (drawer, cart page) to redraw from the live cart. */
async function refreshCartUI() {
  const routes = window.Theme?.routes || {};
  const cart = await fetch(`${(routes.cart_url || '/cart').replace(/\.js$/, '')}.js`, { cache: 'no-store' }).then(
    (response) => response.json()
  );
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'standard-action',
      lines: [],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, source: 'stkr-free-gift-refresh', didError: false },
  });
}

function installFreeGiftDrawerRefresh() {
  if (!window.STKRCart?.freeGiftDrawerRefresh) return;

  /** @param {any} reloader */
  const patch = (reloader) => {
    if (!reloader || reloader.__stkrPatched || typeof reloader.reloadDrawerCartWithoutRefresh !== 'function') {
      return reloader;
    }
    const original = reloader.reloadDrawerCartWithoutRefresh;
    reloader.reloadDrawerCartWithoutRefresh = function (...args) {
      if (!document.querySelector('cart-items-component')) return original.apply(this, args);
      refreshCartUI().catch((error) => console.warn('[stkr-cart-upsell] cart refresh failed', error));
      return false; // handled: tells the app not to reload the page
    };
    reloader.__stkrPatched = true;
    return reloader;
  };

  let current = window.__docappCartReload;
  if (current) {
    patch(current);
    return;
  }
  try {
    Object.defineProperty(window, '__docappCartReload', {
      configurable: true,
      enumerable: true,
      get: () => current,
      set: (value) => {
        current = patch(value);
      },
    });
  } catch (error) {
    console.warn('[stkr-cart-upsell] could not hook Dr Free Gift refresh', error);
  }
}

installFreeGiftDrawerRefresh();

// The BISS backorder reconciler rewrites cart line properties with raw cart
// requests; redraw the drawer when it reports a change.
document.addEventListener('biss:CartBackorderReconciled', (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.changed && document.querySelector('cart-items-component')) {
    refreshCartUI().catch((error) => console.warn('[stkr-cart-upsell] cart refresh failed', error));
  }
});

if (!customElements.get('stkr-keep-children')) customElements.define('stkr-keep-children', StkrKeepChildren);
if (!customElements.get('stkr-cart-upsell')) customElements.define('stkr-cart-upsell', StkrCartUpsell);
